import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, of, switchMap } from 'rxjs';
import { CommonService } from 'src/@services/common.service';
import { CustomerType, EndPoints } from 'src/@shared/shared/enum';
import { findSettingsPermissionGroup } from 'src/@shared/shared/permission-header.util';

// Supplier's own Settings group holds only Users and Notifications, so this portal has always
// read PayBev's group where the role has one. Preserved deliberately; changing it is out of scope.
const SETTINGS_MODULE_TYPES = ['Paybev', 'Supplier'];

@Injectable({
  providedIn: 'root',
})
export class InnerPagesService {
  rolePermissions: any;
  siteName: string = '';
  siteProcessFee: number = 0;
  currentData: any;
  isCompleted = false;
  currentUrl: string;
  previousUrl: string;
  constructor(private _cS: CommonService, private _router: Router) {
    this.currentUrl = this._router.url;
    _router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.previousUrl = this.currentUrl;
        this.currentUrl = event.url;
      }
    });
  }
  /*users*/
  getUsers(requestBody: any) {
    return this._cS.API_POST(EndPoints.GetUsers, requestBody, false);
  }

  //upload delete file
  fileUpload(userBody: any) {
    return this._cS.API_POSTFileUpload(EndPoints.uploadFile, userBody);
  }
  updateUserProfile(userBody: any) {
    const hd = this._cS.GetAuthorizationHeader();
    return this._cS.API_POST(EndPoints.UpdateUserProfile, userBody, false, {
      headers: hd,
    });
  }

  manageProfilePicture(formData: FormData) {
    return this._cS.API_POSTFileUpload(EndPoints.UploadProfilePicture, formData);
  }

  fileDelete(userBody: any) {
    return this._cS.API_POST(EndPoints.deleteFile, userBody);
  }

  getUserRecord(userId: any) {
    return this._cS.API_GET(EndPoints.GetRecordById + `/${userId}`, false);
  }
  //delete unit
  deleteUser(userId: any) {
    return this._cS.API_DELETE(EndPoints.DeleteUser + `/${userId}`);
  }
  /*roles*/
  getAllRoles(roleBody: any) {
    return this._cS.API_GET(EndPoints.GetRoles, roleBody, false);
  }

  getRolePermissionsDetailsFromLocalStorage() {
    this.rolePermissions = JSON.parse(
      localStorage.getItem('RolePermissions') || '[]'
    );
  }

  getUserAccountsSites(searchText: string = '') {
    return this._cS.API_GET(
      EndPoints.GetAccountSupplierAndSupplierBank + '?searchText=' + searchText,
      false
    );
  }
  getSiteAgencyDetails(supplierId: any) {
    return this._cS.API_GET(
      EndPoints.GetSiteAndAgencyDetails + `?supplierId=${supplierId}`,
      false
    );
  }

  /*notifications*/
  getNotificationsDetails(supplierId: any) {
    return this._cS.API_GET(
      EndPoints.GetNotificationDetails + `/${supplierId}`,
      false
    );
  }

  updateNotificationRecord(applyToAll: any, notificationDetails: any) {
    return this._cS.API_POST(
      EndPoints.UpdateNotificationRecord + `/${applyToAll}`,
      notificationDetails,
      false
    );
  }

  /*programs*/
  getAllPrograms(requestBody: any) {
    return this._cS.API_POST(EndPoints.GetAllPrograms, requestBody, false);
  }
  deleteProgram(programId: any) {
    return this._cS.API_DELETE(EndPoints.DeleteProgram + `/${programId}`);
  }
  makeProgramDefault(formData: any) {
    const hd = this._cS.GetAuthorizationHeader();
    return this._cS.API_POST(EndPoints.makeProgramDefault, formData, false, {
      headers: hd,
    });
  }
  updateProgramVisible(formData: any) {
    const hd = this._cS.GetAuthorizationHeader();
    return this._cS.API_POST(EndPoints.UpdatePorgramVisible, formData, false, {
      headers: hd,
    });
  }

  /*site*/
  getSiteNonAlcBalance(supplierId: any) {
    return this._cS.API_GET(
      EndPoints.GetSiteNonAlcBalance + `?supplierId=${supplierId}`,
      false
    );
  }
  /*Investors*/
  getAllAddedInvestors(requestBody: any) {
    return this._cS.API_POST(
      EndPoints.GetAllAddedInvestors,
      requestBody,
      false
    );
  }

  deleteInvestor(investorId: any) {
    return this._cS.API_DELETE(EndPoints.DeleteInvestor + `/${investorId}`);
  }

  searchInDatabaseInvestor(searchText: any, supplierId: any) {
    return this._cS.API_GET(
      EndPoints.GetDBInvestors +
      `?searchText=${searchText}&supplierId=${supplierId}`,
      false
    );
  }

  AddInvestorFromDB(formdata: any) {
    const hd = this._cS.GetAuthorizationHeader();
    return this._cS.API_POST(EndPoints.AddInvestorFromDB, formdata, false, {
      headers: hd,
    });
  }

  getInvestorsForDropdown(supplierId: any) {
    return this._cS.API_GET(
      EndPoints.GetInvestorsBySite + `?supplierId=${supplierId}`,
      false
    );
  }

  getInvestorDetails(investorId: any, isSupplierRequest: boolean) {
    return this._cS.API_GET(
      EndPoints.GetInvestorDetails + `?InvestorId=${investorId}&isSupplierRequest=${isSupplierRequest}`,
      false
    );
  }

  /*investments*/
  getInvestments(
    supplierId: any,
    InvestorId: any,
    searchText: any,
    sortOrder: any,
    sortField: any,
    page: any,
    pageSize: any,
    filterByStatus: any
  ) {
    return this._cS.API_GET(
      EndPoints.GetAllInvestments +
      `?supplierId=${supplierId}&InvesterId=${InvestorId}&searchText=${searchText}&sortOrder=${sortOrder}&sortField=${sortField}&page=${page}&pageSize=${pageSize}&filterByStatus=${filterByStatus}`,
      false
    );
  }

  confirmInvestment(formdata: any) {
    const hd = this._cS.GetAuthorizationHeader();
    return this._cS.API_POST(EndPoints.ConfirmInvestment, formdata, false, {
      headers: hd,
    });
  }

  editPDFFiles(file: any, page: any, EditAction: any, rotateDeg: any) {
    const hd = this._cS.GetAuthorizationHeader();
    return this._cS.API_POST(
      EndPoints.EditPDFFiles +
      `?page=${page}&EditAction=${EditAction}&rotateDeg=${rotateDeg}`,
      file,
      false,
      {
        headers: hd,
      }
    );
  }

  getVendorDropdownList(supplierId: any) {
    return this._cS.API_GET(
      EndPoints.GetVendorDropdownList + `?supplierId=${supplierId}`,
      false
    );
  }
  getVendorDetails(vendorId: any) {
    return this._cS.API_GET(
      EndPoints.GetVendorDetails + `?VendorId=${vendorId}`,
      false
    );
  }

  schedulePayment(paymentData: any) {
    const hd = this._cS.GetAuthorizationHeader();
    return this._cS.API_POST(EndPoints.SchedulePayment, paymentData, false, {
      headers: hd,
    });
  }

  getPaymentDetails(paymentId: any) {
    return this._cS.API_GET(
      EndPoints.GetPayment + `?PaymentId=${paymentId}`,
      false
    );
  }

  getInvestmentHistory(investorId: any, supplierId: any) {
    return this._cS.API_GET(
      EndPoints.GetInvestmentHistory +
      `?parentInvestorId=${investorId}&supplierId=${supplierId}`,
      false
    );
  }

  /*tags*/
  getAllTags(requestBody: any) {
    return this._cS.API_POST(EndPoints.GetAllTags, requestBody, false);
  }

  getTagsDropdownList(supplierId: any) {
    return this._cS.API_GET(
      EndPoints.GetTagsDropdownlist + `?supplierId=${supplierId}`,
      false
    );
  }

  logOut(): Observable<any> {
    // 1) First call: no custom headers, interceptor will inject `BearerToken`
    const firstCall$ = this._cS.API_GET(EndPoints.LogOut, false);

    // 2) Prepare second call: only if PaybevBearerToken exists
    const supplierToken = localStorage.getItem('PaybevBearerToken');
    if (supplierToken) {
      const headers = new HttpHeaders().set(
        'Authorization',
        `Bearer ${supplierToken}`
      );
      const secondCall$ = supplierToken
        ? this._cS.API_GET(EndPoints.LogOut, false, { headers })
        : of(null); // no-op if no supplier token

      // Chain them: firstCall$ → then secondCall$, emitting only when both complete
      return firstCall$.pipe(switchMap(() => secondCall$));
    } else {
      return firstCall$;
    }
  }

  addTagGroup(tagGroupData: any) {
    return this._cS.API_POST(EndPoints.AddTagGroup, tagGroupData, false);
  }
  updateTagGroup(tagGroupData: any) {
    return this._cS.API_POST(EndPoints.UpdateTagGroup, tagGroupData, false);
  }
  deleteTagGroup(groupId: any) {
    return this._cS.API_DELETE(EndPoints.DeleteTagGroup + `/${groupId}`);
  }
  addTag(tagData: any) {
    return this._cS.API_POST(EndPoints.AddTag, tagData, false);
  }
  updateTag(tagData: any) {
    return this._cS.API_POST(EndPoints.UpdateTag, tagData, false);
  }
  deleteTag(groupId: any, tagId: any) {
    return this._cS.API_DELETE(EndPoints.DeleteTag + `/${groupId}/${tagId}`);
  }

  getTagGroupNameList(supplierId: any) {
    return this._cS.API_GET(
      EndPoints.GetTagGroupName + `?supplierId=${supplierId}`,
      false
    );
  }

  getUserProfileData() {
    return this._cS.API_GET(EndPoints.GetPaybevUserDetails, false);
  }

  updateProfileData(profileData: any) {
    return this._cS.API_POST(EndPoints.UpdateProfileData, profileData, false);
  }
  /*get activities */
  getActvityRecordsV2(
    supplierId: any,
    qboSupplierId: any,
    startPeriod: any,
    endPeriod: any,
    searchText: any,
    TransactionSubType: any,
    activityType: any,
    sortOrder: any,
    sortField: any,
    page: any,
    pageSize: any,
    isNonAlc: boolean,
    filterStatus: any,
    filterTransactions: any,
    filterProgram: any,
    filterTag: any,
    filterReps: any,
    customerType: number
  ) {
    let queryParams;
    if (activityType == 3) {
      queryParams = {
        SupplierId: supplierId,
        StartPeriod: startPeriod,
        EndPeriod: endPeriod,
        SearchText: searchText,
        TransactionSubType: TransactionSubType,
        ActivityType: activityType,
        SortOrder: sortOrder,
        SortField: sortField,
        Page: page,
        PageSize: pageSize,
        isNonAlc: isNonAlc,
        transactionTypes: filterTransactions,
        siteIds: filterProgram,
        tagIds: filterTag,
        companyDisplayName: filterReps,
        qBOSupplierId: qboSupplierId,
        customerType: customerType
      };
    } else {
      queryParams = {
        SupplierId: supplierId,
        SearchText: searchText,
        TransactionSubType: TransactionSubType,
        ActivityType: activityType,
        SortOrder: sortOrder,
        SortField: sortField,
        Page: page,
        PageSize: pageSize,
        isNonAlc: isNonAlc,
        Status: filterStatus,
        siteIds: filterProgram,
        qBOSupplierId: qboSupplierId,
        companyDisplayName: filterReps,
        customerType: customerType
      };
    }
    return this._cS.API_POST(EndPoints.GetActivitiesV2, queryParams, false);
  }

  getStandardContent(
    programId: any,
    searchText: any,
    sortField: any,
    sortOrder: any,
    isUnpurchasedRequested: boolean,
    page: any,
    pageSize: any,
    brandIds: string[],
    statusIds: number[],
    customerType: number
  ) {
    const requestBody = {
      brandIds: brandIds.join(","),
      programId: programId,
      statusIds: statusIds,
      searchText: searchText,
      sortField: sortField,
      sortOrder: sortOrder,
      page: page,
      pageSize: pageSize,
      isUnpurchasedRequested: isUnpurchasedRequested,
      customerType: customerType
    };
    return this._cS.API_POST(
      EndPoints.GetStandardContent,
      requestBody,
      false
    );
  }
  getPremiumContent(
    brandIds: string[],
    programId: any,
    repIds: string[],
    statusIds: number[],
    searchText: any,
    sortField: any,
    sortOrder: any,
    page: any,
    pageSize: any,
    isPastDueSelected: any
  ) {
    const requestBody = {
      brandIds: brandIds.join(","),
      programId: programId,
      repIds: repIds.join(","),
      statusIds: statusIds,
      searchText: searchText,
      sortField: sortField,
      sortOrder: sortOrder,
      page: page,
      pageSize: pageSize,
      isPastDue: isPastDueSelected
    };

    return this._cS.API_POST(
      EndPoints.GetPremiumContent,
      requestBody,
      false
    );
  }

  getBrandsForFilter(programId: any) {
    return this._cS.API_GET(
      EndPoints.GetBrandsForFilter + `?programId=${programId}`,
      false
    );
  }

  getPremiumContentFilter(programId: any) {
    return this._cS.API_GET(
      EndPoints.GetPremiumContentFilter + `?programId=${programId}`,
      false
    );
  }

  getBrandReport(brandId: string) {
    const hd = this._cS.GetAuthorizationHeader();
    return this._cS.API_GET(
      EndPoints.GetBrandReportPdf + `/${brandId}`,
      false,
      {
        responseType: 'arraybuffer',
        observe: 'response',
        headers: hd,
      }
    );
  }

  downloadAllBrandReports(
    brandIds: string[],
    programId: any,
    repIds: string[],
    statusIds: number[],
    searchText: any,
    sortField: any,
    sortOrder: any,
    page: any,
    pageSize: any,
    isPastDueSelected: any,
    clientDateTime: string
  ) {
    const requestBody = {
      brandIds: brandIds.join(','),
      programId: programId,
      repIds: repIds.join(','),
      statusIds: statusIds,
      searchText: searchText,
      sortField: sortField,
      sortOrder: sortOrder,
      page: page,
      pageSize: pageSize,
      isPastDue: isPastDueSelected,
      clientDateTime: clientDateTime,
    };
    const hd = this._cS.GetAuthorizationHeader();
    return this._cS.API_POST(
      EndPoints.DownloadAllBrandReports,
      requestBody,
      false,
      {
        responseType: 'arraybuffer',
        observe: 'response',
        headers: hd,
      }
    );
  }

  getStandardContentFilters(programId: any) {
    return this._cS.API_GET(
      EndPoints.GetStandardContentFilters + `?programId=${programId}`,
      false
    );
  }

  getActivityCounts(supplierId: any) {
    return this._cS.API_GET(
      EndPoints.GetActivityCounts + `?supplierId=${supplierId}`,
      false
    );
  }
  getActivitiesCounts(requestBody: any) {
    return this._cS.API_POST(EndPoints.GetActivitiesCounts, requestBody, false);
  }
  getSummary(supplierId: any, qboSupplierId, customerType: boolean) {
    if (qboSupplierId) {
      return this._cS.API_GET(
        EndPoints.GetSummary +
        `?supplierId=${supplierId}&qBOSupplierId=${qboSupplierId ? qboSupplierId : ''}&customerType=${customerType}`,
        false
      );
    } else {
      return this._cS.API_GET(
        EndPoints.GetSummary +
        `?supplierId=${supplierId}&customerType=${customerType}`,
        false
      );
    }
  }
  getResources() {
    return this._cS.API_GET(
      EndPoints.GetResourceRecords +
      `?startPosition=${1}&pageSize=${-1}&sortOrder=${'asc'}&sortField=${'OrderNo'}&resourceType=${1}`,
      false
    );
  }

  contactInvestor(requestBody: any) {
    return this._cS.API_POST(EndPoints.ContactInvestor, requestBody, false);
  }

  //reports
  getLast24Months(supplierId: any) {
    return this._cS.API_GET(
      EndPoints.GetLast24Months + `?supplierId=${supplierId}`,
      false
    );
  }

  getAccountStatements(supplierId: any, year: any, month: any) {
    return this._cS.API_GET(
      EndPoints.GetAccountStatements +
      `?supplierId=${supplierId}&StatementYear=${year}&StatementMonth=${month}`,
      false
    );
  }
  getOutstandingInvoices(supplierId: any) {
    return this._cS.API_GET(
      EndPoints.GetOutstandingInvoices + `?supplierId=${supplierId}`,
      false
    );
  }
  //reports
  getActivityExport(
    supplierId: any,
    qboSupplierId: string,
    startPeriod: any,
    endPeriod: any
  ) {
    const hd = this._cS.GetAuthorizationHeader();
    return this._cS.API_GET(
      EndPoints.GetActivityExport +
      `?supplierId=${supplierId}&QBOSupplierId=${qboSupplierId}&StartPeriod=${startPeriod}&EndPeriod=${endPeriod}&SortOrder=desc&SortField=createdOn`,
      false,
      {
        responseType: 'blob',
        headers: hd,
      }
    );
  }

  //announcement
  getAnnouncements(supplierId: any) {
    return this._cS.API_GET(
      EndPoints.GetAnnouncement + `?supplierId=${supplierId}`,
      false
    );
  }

  clearAnnouncements(requestBody: any) {
    return this._cS.API_POST(
      EndPoints.UpdateSeenAnnouncement,
      '"' + requestBody + '"',
      false
    );
  }

  openViewInvoiceURL(transactionsId: any, transactionsType: any) {
    return this._cS.API_GET(
      EndPoints.GetInvoiceURL +
      `?transactionsId=${transactionsId}&transactionsType=${transactionsType}`,
      false
    );
  }

  //check if current user can access users
  canAccessUsers() {
    this.getRolePermissionsDetailsFromLocalStorage();
    let canViewUsers, canAddUser, canEditUser, canDeleteUser;
    const adminDetails = findSettingsPermissionGroup(this.rolePermissions, SETTINGS_MODULE_TYPES);
    const leaf = adminDetails?.permissions?.find((obj: any) => obj.module === 'Users');
    if (leaf) {
      canViewUsers = leaf?.canView;
      canAddUser = leaf?.canAdd;
      canEditUser = leaf?.canEdit;
      canDeleteUser = leaf?.canDelete;
    }
    return { canViewUsers, canAddUser, canEditUser, canDeleteUser };
  }

  //check if current user can access payments
  canAccessPayments() {
    this.getRolePermissionsDetailsFromLocalStorage();
    let canAddPayment, canDeletePayment;
    const paymentDetails = this.rolePermissions[3];
    const leaf = paymentDetails?.permissions?.find((obj: any) => obj.module === 'Expenses');
    if (leaf) {
      canAddPayment = leaf.canAdd;
      canDeletePayment = leaf.canDelete;
    }
    return { canAddPayment, canDeletePayment };
  }

  canAccessInvestments() {
    this.getRolePermissionsDetailsFromLocalStorage();
    let canViewInvestments,
      canAddInvestment,
      canEditInvestment,
      canDeleteInvestment;
    const investmentDetails = this.rolePermissions[2];
    const leaf = investmentDetails?.permissions?.find((obj: any) => obj.module === 'Investments');
    if (leaf) {
      canViewInvestments = leaf.canView;
      canAddInvestment = leaf.canAdd;
      canEditInvestment = leaf.canEdit;
      canDeleteInvestment = leaf.canDelete;
    }
    return {
      canViewInvestments,
      canAddInvestment,
      canEditInvestment,
      canDeleteInvestment,
    };
  }

  canAccessSummary() {
    this.getRolePermissionsDetailsFromLocalStorage();
    let canViewSummary, canAddSummary, canDeleteSummary;
    const summaryDetails = this.rolePermissions[0];

    const index = summaryDetails?.permissions?.findIndex(
      (obj: any) => obj.module == 'Summary'
    );

    if (index != undefined && index != -1) {
      canViewSummary = summaryDetails.permissions[index].canView;
      canAddSummary = summaryDetails.permissions[index].canAdd;
      canDeleteSummary = summaryDetails.permissions[index].canDelete;
    }

    return {
      canViewSummary,
      canAddSummary,
      canDeleteSummary,
    };
  }
  canAccessActivity() {
    this.getRolePermissionsDetailsFromLocalStorage();
    let canViewActivity, canAddActivity;
    const ActivityDetails = this.rolePermissions[1];
    const index = ActivityDetails?.permissions?.findIndex(
      (obj: any) => obj.module == 'Activity'
    );
    if (index != undefined && index != -1) {
      canViewActivity = ActivityDetails.permissions[index].canView;
      canAddActivity = ActivityDetails.permissions[index].canAdd;
    }
    return {
      canViewActivity,
      canAddActivity,
    };
  }
  canAccessPrograms() {
    this.getRolePermissionsDetailsFromLocalStorage();
    let canViewPrograms, canAddPrograms, canEditPrograms, canDeletePrograms;
    const ProgramsDetails = this.rolePermissions[2];
    const index = ProgramsDetails?.permissions?.findIndex(
      (obj: any) => obj.module == 'RetailProgram'
    );
    if (index != undefined && index != -1) {
      canViewPrograms = ProgramsDetails.permissions[index].canView;
      canAddPrograms = ProgramsDetails.permissions[index].canAdd;
      canEditPrograms = ProgramsDetails.permissions[index].canEdit;
      canDeletePrograms = ProgramsDetails.permissions[index].canDelete;
    }
    return {
      canViewPrograms,
      canAddPrograms,
      canEditPrograms,
      canDeletePrograms,
    };
  }
  canAccessInvestors() {
    this.getRolePermissionsDetailsFromLocalStorage();
    let canViewInvestors, canAddInvestor, canDeleteInvestor;
    const adminDetails = findSettingsPermissionGroup(this.rolePermissions, SETTINGS_MODULE_TYPES);
    const leaf = adminDetails?.permissions?.find((obj: any) => obj.module === 'Investors');
    if (leaf) {
      canViewInvestors = leaf?.canView;
      canAddInvestor = leaf?.canAdd;
      canDeleteInvestor = leaf?.canDelete;
    }
    return {
      canViewInvestors,
      canAddInvestor,
      canDeleteInvestor,
    };
  }
  /**
   * Tags is its own permission once the registry split lands. Program period screens keep using
   * canAccessProgramsTags(); only the tag grid reads this one.
   */
  canAccessTags() {
    this.getRolePermissionsDetailsFromLocalStorage();
    let canViewTags, canAddTag, canEditTag, canDeleteTag;
    const adminDetails = findSettingsPermissionGroup(this.rolePermissions, SETTINGS_MODULE_TYPES);
    const leaf = adminDetails?.permissions?.find((obj: any) => obj.module === 'Tags');
    if (leaf) {
      canViewTags = leaf?.canView;
      canAddTag = leaf?.canAdd;
      canEditTag = leaf?.canEdit;
      canDeleteTag = leaf?.canDelete;
    }
    return { canViewTags, canAddTag, canEditTag, canDeleteTag };
  }

  canAccessProgramsTags() {
    this.getRolePermissionsDetailsFromLocalStorage();
    let canViewProgramsTags,
      canAddProgramTag,
      canEditProgramTag,
      canDeleteProgramTag;
    const adminDetails = findSettingsPermissionGroup(this.rolePermissions, SETTINGS_MODULE_TYPES);
    const leaf = adminDetails?.permissions?.find((obj: any) => obj.module === 'Program Periods');
    if (leaf) {
      canViewProgramsTags = leaf?.canView;
      canAddProgramTag = leaf?.canAdd;
      canEditProgramTag = leaf?.canEdit;
      canDeleteProgramTag = leaf?.canDelete;
    }
    return {
      canViewProgramsTags,
      canAddProgramTag,
      canEditProgramTag,
      canDeleteProgramTag,
    };
  }
  canAccessTutorials() {
    this.getRolePermissionsDetailsFromLocalStorage();
    let canViewTutorials;
    //  && obj.moduleType == 'Supplier'
    const adminDetails = findSettingsPermissionGroup(this.rolePermissions, SETTINGS_MODULE_TYPES);
    const leaf = adminDetails?.permissions?.find((obj: any) => obj.module === 'Tutorials');
    if (leaf) {
      canViewTutorials = leaf?.canView;
    }
    return canViewTutorials;
  }
  canAccessNotifications() {
    this.getRolePermissionsDetailsFromLocalStorage();
    let canViewNotifications, canEditNotification, canDeleteNotification;
    //  && obj.moduleType == 'Supplier'
    const adminDetails = findSettingsPermissionGroup(this.rolePermissions, SETTINGS_MODULE_TYPES);
    const leaf = adminDetails?.permissions?.find((obj: any) => obj.module === 'Notifications');
    if (leaf) {
      canViewNotifications = leaf?.canView;
      canEditNotification = leaf?.canEdit;
      canDeleteNotification = leaf?.canDelete;
    }
    return {
      canViewNotifications,
      canEditNotification,
      canDeleteNotification,
    };
  }
  canAccessReports() {
    this.getRolePermissionsDetailsFromLocalStorage();
    let canViewReports;
    const reportsDetails = this.rolePermissions[3];
    const index = reportsDetails?.permissions?.findIndex(
      (obj: any) => obj.module == 'Reports'
    );
    if (index != undefined && index != -1) {
      canViewReports = reportsDetails.permissions[index].canView;
    }
    return canViewReports;
  }

  /** PayBev's Summary permission, read from `PaybevRolePermissions` (not the supplier `RolePermissions`). */
  canAccessPaybevSummary() {
    let canViewSummary, canAddSummary, canDeleteSummary;
    let paybevPermissions: any[] = [];
    try {
      paybevPermissions = JSON.parse(localStorage.getItem('PaybevRolePermissions') || '[]');
    } catch {
      paybevPermissions = [];
    }
    const summary = paybevPermissions
      .flatMap((group: any) => group?.permissions ?? [])
      .find((obj: any) => obj.module == 'Summary');

    if (summary) {
      canViewSummary = summary.canView;
      canAddSummary = summary.canAdd;
      canDeleteSummary = summary.canDelete;
    }

    return {
      canViewSummary,
      canAddSummary,
      canDeleteSummary,
    };
  }

  canAccessContentManager() {
    this.getRolePermissionsDetailsFromLocalStorage();
    let canViewContent, canAddContent, canDeleteContent;

    // Find all Supplier moduleTypes and search through their permissions
    const supplierModules = this.rolePermissions.filter(
      (obj: any) => obj.moduleType === 'Supplier'
    );

    // Search through all Supplier modules to find RetailProgram
    for (const supplierModule of supplierModules) {
      if (supplierModule.permissions) {
        const retailProgramPermission = supplierModule.permissions.find(
          (obj: any) => obj.module === 'RetailProgram'
        );

        if (retailProgramPermission) {
          canViewContent = retailProgramPermission.canView;
          canAddContent = retailProgramPermission.canAdd;
          canDeleteContent = retailProgramPermission.canDelete;
          break; // Found it, no need to continue searching
        }
      }
    }

    return {
      canViewContent,
      canAddContent,
      canDeleteContent,
    };
  }

  //Code Added by Dev Dasani (08/04/2025)
  getAssignmentAndAgreementDetails() {
    return this._cS.API_GET(EndPoints.GetAssignmentAndAgreementDate, false);
  }

  getSupplierActivityFilters(
    supplierId: any,
    qboSupplierId: any,
    startPeriod: any,
    endPeriod: any,
    // searchText: any,
    TransactionSubType: any,
    activityType: any,
    sortOrder: any,
    sortField: any,
    page: any,
    pageSize: any,
    isNonAlc: boolean,
    filterStatus: any,
    customerType: number
    // filterTransactions: any,
    // filterProgram: any,
    // filterTag: any
  ) {
    let queryParams;
    if (activityType == 3) {
      queryParams = {
        SupplierId: supplierId,
        StartPeriod: startPeriod,
        EndPeriod: endPeriod,
        SearchText: null,
        TransactionSubType: TransactionSubType,
        ActivityType: activityType,
        SortOrder: sortOrder,
        SortField: sortField,
        Page: page,
        PageSize: pageSize,
        isNonAlc: isNonAlc,
        qBOSupplierId: qboSupplierId,
        customerType: customerType
      };
    } else {
      queryParams = {
        SupplierId: supplierId,
        SearchText: null,
        TransactionSubType: TransactionSubType,
        ActivityType: activityType,
        SortOrder: sortOrder,
        SortField: sortField,
        Page: page,
        PageSize: pageSize,
        isNonAlc: isNonAlc,
        Status: filterStatus,
        // siteIds: filterProgram,
        qBOSupplierId: qboSupplierId,
        customerType: customerType
      };
    }

    return this._cS.API_POST(
      EndPoints.GetSupplierActivitiesFilter,
      queryParams,
      false
    );
  }

  getSupplierPrograms() {
    return this._cS.API_GET(EndPoints.GetSupplierPrograms, false);
  }
  /*************  ✨ Windsurf Command ⭐  *************/
  /**
   * Gets the site details by siteId
   * @param {any} siteId - The siteId to get the site details for
   * @returns {Observable<any>} - An observable containing the site details
   */
  /*******  f1016e20-4ffb-4b59-95a2-2a68bbbdd98c  *******/
  getSiteDetailsById(supplierId: any) {
    return this._cS.API_GET(
      EndPoints.GetSiteDetailsById + `?supplierId=${supplierId}`,
      false
    );
  }
  getRetailProgramById(programId: any) {
    return this._cS.API_POST(
      EndPoints.GetRetailProgramById + `/${programId}`,
      false
    );
  }

  //faqs
  getFAQs() {
    return this._cS.API_GET(
      EndPoints.GetFAQList,
      false
    );
  }

  getSiteContentManager(supplierId: any) {
    return this._cS.API_GET(
      EndPoints.GetContentManager + `?supplierId=${supplierId}`
    );
  }
  addContentManager(contentManager: any) {
    return this._cS.API_POST(
      EndPoints.AddContentManager,
      contentManager,
      false
    );
  }
  updateContentManager(contentManager: any) {
    return this._cS.API_POST(
      EndPoints.UpdateContentManager,
      contentManager,
      false
    );
  }
  deleteContentManager(userId: any) {
    return this._cS.API_DELETE(EndPoints.DeleteContentManager + `/${userId}`);
  }
  deleteInvestment(id: any) {
    return this._cS.API_DELETE(
      EndPoints.DeleteRetailProgramInvestment + `/${id}`
    );
  }
  getInvestor(supplierId: any) {
    return this._cS.API_GET(
      EndPoints.GetInvestor + `?id=${supplierId}&isSupplierRequest=true`,
      false
    );
  }
}
